import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDownward,
  ArrowUpward,
  HorizontalRule,
  Search as SearchIcon,
  Widgets,
} from "@mui/icons-material";
import {
  Box,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { LineChart, ScatterChart } from "echarts/charts";
import { GridComponent, LegendComponent, TitleComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import ReactECharts from "echarts-for-react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import AutocompletePaginated from "../../../components/autocomplete/autocompletePaginated";
import Loading from "../../../components/loading/loading";
import { AiDemandForecastResponse, AiDemandForecastResult } from "../../../models/general/ai-demand-forecast";
import { Analysis } from "../../../models/general/analysis";
import { Bom } from "../../../models/general/bom";
import { BomComponent } from "../../../models/general/bom-component";
import { URLS } from "../../../services/app-urls";
import { BaseService } from "../../../services/base-service";
import { translate } from "../../../translate/translate";
import { formatCompactNumber, parseLocaleNumber } from "../../../utils/locale-number-utils";
import InfoCardComponent from "../dashboard/components/info-card.component";

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  LineChart,
  ScatterChart,
  CanvasRenderer,
]);

export interface AiDemandForecastPanelProps {
  allowModelFilter?: boolean;
}

const AiDemandForecastPanel = ({ allowModelFilter = false }: AiDemandForecastPanelProps) => {
  const service = useMemo(() => new BaseService<Analysis>(URLS.ANALYSIS_IA), []);
  const bomComponentService = useMemo(() => new BaseService<BomComponent>(URLS.BOM_COMPONENT), []);
  const serviceBom = useMemo(() => new BaseService<Bom>(URLS.BOM), []);
  const [bomComponent, setBomComponent] = useState<BomComponent>();
  const [bomFinishedGood, setBomFinishedGood] = useState<Bom | null>(null);
  const [filterType, setFilterType] = useState<"part_number" | "model">("part_number");
  const [clearAutocomplete, setClearAutocomplete] = useState(false);
  const [modelTouched, setModelTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<AiDemandForecastResult | null>();
  const secondFieldRef = useRef<HTMLDivElement>(null);
  const bomComponentFilters = useMemo(() => [{ mtype: BomComponent.MType.ZROH }], []);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      secondFieldRef.current?.querySelector("input")?.focus();
    }, 500);
    return () => window.clearTimeout(timeout);
  }, []);

  const handleChangeFilterType = (event: SelectChangeEvent<"part_number" | "model">) => {
    setFilterType(event.target.value as "part_number" | "model");
    setClearAutocomplete(true);
    setBomComponent(undefined);
    setBomFinishedGood(null);
    setData(undefined);
    setModelTouched(false);
    window.setTimeout(() => secondFieldRef.current?.querySelector("input")?.focus(), 100);
  };

  const search = () => {
    service.clearParameters();

    if (filterType === "part_number") {
      const componentId = bomComponent?.component?.id;
      if (!componentId) {
        toast.warning(translate.t("select_part_number", { defaultValue: "Selecione um Part Number" }));
        return;
      }
      service.addParameter("bom_component", componentId);
    } else {
      if (!allowModelFilter || !bomFinishedGood) {
        setModelTouched(true);
        toast.warning(translate.t("select_model", { defaultValue: "Selecione um Modelo" }));
        return;
      }
      service.addParameter("model", bomFinishedGood.bom_number);
    }

    setIsLoading(true);
    service.getAllFromListRoute("result-data-ia")
      .then(response => {
        const responseData = response.data as unknown as AiDemandForecastResponse;
        setData(responseData.results);
      })
      .catch(error => toast.error(translate.t("unknown_error"), error))
      .finally(() => setIsLoading(false));
  };

  const whichIcon = (value: number | null | undefined) => {
    if (value === null || value === undefined) return <HorizontalRule color="action" fontSize="large" />;
    if (value < 0) return <ArrowDownward color="error" fontSize="large" />;
    if (value > 0) return <ArrowUpward color="success" fontSize="large" />;
    return <HorizontalRule color="action" fontSize="large" />;
  };

  const getOption = () => {
    const confidenceIntervalName = translate.t("dashboard_page.ic_trust");
    const confidenceIntervalColor = "rgba(30,144,255,0.18)";
    const straightLineStyle = { cap: "butt", join: "miter" } as const;
    return {
      tooltip: {
        trigger: "axis",
        formatter: (params: any[]) => {
          const trendName = translate.t("dashboard_page.trend");
          const colorMap: Record<string, string> = {
            [translate.t("dashboard_page.history")]: "green",
            [translate.t("dashboard_page.predicted")]: "orange",
            [confidenceIntervalName]: confidenceIntervalColor,
            [translate.t("dashboard_page.out_of_standard")]: "orange",
            [translate.t("dashboard_page.forecast")]: "blue",
            [trendName]: "red",
          };
          const header = params[0]?.axisValueLabel ? `${params[0].axisValueLabel}<br/>` : "";
          return header + params
            .filter(item => item.data != null && item.data !== "-" && item.seriesName !== translate.t("dashboard_page.out_of_standard"))
            .map(item => {
              const value = item.seriesName === trendName
                ? `${Math.round(data?.trend_percent?.[item.dataIndex] ?? 0)}%`
                : parseLocaleNumber(item.data);
              const color = colorMap[item.seriesName] || item.color;
              return `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background:${color};"></span>
                ${item.seriesName}: <b>${value}</b>`;
            }).join("<br/>");
        },
      },
      legend: {
        data: [
          { name: translate.t("dashboard_page.history"), itemStyle: { opacity: 0 } },
          { name: translate.t("dashboard_page.predicted"), itemStyle: { opacity: 0 } },
          { name: confidenceIntervalName, icon: "rect", itemStyle: { color: confidenceIntervalColor } },
          translate.t("dashboard_page.out_of_standard"),
          translate.t("dashboard_page.forecast"),
          { name: translate.t("dashboard_page.trend"), itemStyle: { opacity: 0 } },
        ],
      },
      xAxis: { type: "category", data: data?.dates.map(date => dayjs(date).format("YYYY-MM-DD")) },
      yAxis: { type: "value", axisLabel: { formatter: (value: number) => formatCompactNumber(value) } },
      series: [
        {
          name: confidenceIntervalName, type: "line", data: data?.hist_upper, smooth: false,
          lineStyle: { opacity: 0 }, symbol: "none",
          areaStyle: { color: confidenceIntervalColor, origin: "start" },
          silent: true, z: -1,
          tooltip: { trigger: "item", valueFormatter: (value: number | null) => value != null ? value : "" },
          emphasis: { disabled: true }, select: { disabled: true }, blur: { disabled: true }, focus: "none",
        },
        {
          name: confidenceIntervalName, type: "line", data: data?.hist_lower, smooth: false,
          lineStyle: { opacity: 0 }, symbol: "none", areaStyle: { color: "#fff", opacity: 1, origin: "start" },
          z: -1, tooltip: { show: false }, showInLegend: false,
        },
        {
          name: confidenceIntervalName, type: "line", data: data?.forecast_upper, smooth: false,
          lineStyle: { opacity: 0 }, symbol: "none",
          areaStyle: { color: confidenceIntervalColor, origin: "start" }, z: -1, silent: true,
          tooltip: { trigger: "item", valueFormatter: (value: number | null) => value != null ? value : "" },
          showInLegend: false, emphasis: { disabled: true }, select: { disabled: true },
          blur: { disabled: true }, focus: "none",
        },
        {
          name: confidenceIntervalName, type: "line", data: data?.forecast_lower, smooth: false,
          lineStyle: { opacity: 0 }, symbol: "none", areaStyle: { color: "#fff", opacity: 1, origin: "start" },
          z: -1, tooltip: { show: false }, showInLegend: false,
        },
        {
          name: translate.t("dashboard_page.history"), type: "line", data: data?.final_quantity_smoothed,
          smooth: false, lineStyle: { color: "green", ...straightLineStyle }, symbol: "none",
        },
        {
          name: translate.t("dashboard_page.predicted"), type: "line", data: data?.final_quantity,
          lineStyle: { type: "dashed", color: "orange", ...straightLineStyle }, smooth: false, symbol: "none",
        },
        {
          name: translate.t("dashboard_page.out_of_standard"), type: "scatter", data: data?.outliers_mask,
          symbolSize: 12, itemStyle: { color: "orange", opacity: 1, borderColor: "#FFA500", borderWidth: 2 }, z: 10,
        },
        {
          name: translate.t("dashboard_page.forecast"), type: "line", data: data?.forecast, smooth: false,
          lineStyle: { type: "dashed", color: "blue", ...straightLineStyle }, symbol: "none", showSymbol: false, symbolSize: 0,
          itemStyle: { color: "blue" }, emphasis: { disabled: true },
        },
        {
          name: translate.t("dashboard_page.trend"), type: "line", data: data?.trend, smooth: false,
          lineStyle: { color: "red", ...straightLineStyle }, symbol: "none",
        },
      ],
    };
  };

  const titleStyle = { fontFamily: "Inter", fontWeight: 700, fontSize: "16px", lineHeight: "100%" };
  const noItemText = { fontFamily: "Inter", fontWeight: 600, fontSize: "16px", lineHeight: "100%" };

  return (
    <Box sx={{ marginBottom: "16px", border: "1px solid #e0e0e0", borderRadius: "8px" }}>
      <Loading open={isLoading} />
      <Box sx={{ backgroundColor: "#f9fafc", padding: "15px", borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
        <Typography sx={titleStyle}>{translate.t("continuous_forecasting")}</Typography>
      </Box>
      <Divider />
      <Box sx={{ backgroundColor: "#f9fafc", padding: "15px", display: "flex", gap: "10px", alignItems: "center" }}>
        <FormControl variant="outlined" sx={{ width: "30%" }}>
          <InputLabel id="ai-forecast-filter-type-label">{translate.t("filter_by", { defaultValue: "Filtrar por" })}</InputLabel>
          <Select
            labelId="ai-forecast-filter-type-label"
            value={filterType}
            onChange={handleChangeFilterType}
            label={translate.t("filter_by", { defaultValue: "Filtrar por" })}
          >
            <MenuItem value="part_number">{translate.t("part_number")}</MenuItem>
            {allowModelFilter && <MenuItem value="model">{translate.t("model", { defaultValue: "Modelo" })}</MenuItem>}
          </Select>
        </FormControl>
        <Box sx={{ flexGrow: 1 }} ref={secondFieldRef}>
          {filterType === "part_number" ? (
            <AutocompletePaginated
              service={bomComponentService}
              label={translate.t("part_number")}
              searchField="search"
              display="component.part_number"
              onSelectElement={(component: BomComponent) => { setBomComponent(component); setClearAutocomplete(false); }}
              clear={clearAutocomplete}
              reset={false}
              required
              filters={bomComponentFilters}
              styleProps={{ width: "100%" }}
            />
          ) : (
            <AutocompletePaginated
              service={serviceBom}
              label={translate.t("model", { defaultValue: "Modelo" })}
              searchField="search"
              display="bom_number"
              onSelectElement={(bom: Bom | null) => { setBomFinishedGood(bom); setClearAutocomplete(false); setModelTouched(false); }}
              element={bomFinishedGood}
              clear={clearAutocomplete}
              reset={false}
              required
              forceError={modelTouched && !bomFinishedGood}
              styleProps={{ width: "100%" }}
            />
          )}
        </Box>
        <IconButton aria-label="search" color="primary" onClick={search} sx={{ backgroundColor: "primary.main", color: "white", "&:hover": { backgroundColor: "primary.dark" } }}>
          <SearchIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ backgroundColor: "white", padding: "15px", display: "flex", gap: "10px", minHeight: 500, alignItems: "stretch", borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px" }}>
        <Box sx={{ display: "flex", flex: 8, border: "1px solid #e0e0e0", minHeight: 500, padding: "10px" }}>
          {data ? (
            <ReactECharts option={getOption()} notMerge style={{ height: "100%", width: "100%" }} opts={{ renderer: "canvas" }} />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", justifyContent: "center", width: "100%" }}>
              <Widgets sx={{ width: "106px", height: "106px", color: "#CFD8DC" }} />
              <Typography sx={noItemText}>{translate.t("empty_listing")}</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", flex: 2, flexDirection: "column", height: 500, gap: "10px" }}>
          <InfoCardComponent
            title={translate.t("forecast_long_run")}
            value={data?.demand_last_5_months ? `${Math.round(data.demand_last_5_months)}%` : "-"}
            icon={whichIcon(data?.demand_last_5_months)}
            description={translate.t("low_high_demand", { type: (data?.trend_last_5_months ?? 0) > 0 ? translate.t("dashboard_page.high") : (data?.trend_last_5_months ?? 0) < 0 ? translate.t("dashboard_page.low") : translate.t("dashboard_page.neutral") })}
            styleProps={{ marginBottom: 0, height: "100%", flex: 1 }}
          />
          <InfoCardComponent
            title={translate.t("deviations_anomalies")}
            value={`${data?.outliers_count_last_5_months ?? 0} ${translate.t("occurrences")}`}
            icon={<SearchIcon color="primary" fontSize="large" />}
            description={translate.t("out_or_in_standard", { type: (data?.outliers_count_last_5_months ?? 0) > 0 ? translate.t("dashboard_page.out_of_standard") : translate.t("dashboard_page.standard") })}
            styleProps={{ marginBottom: 0, height: "100%", flex: 1 }}
          />
          <InfoCardComponent
            title={translate.t("dashboard_page.trend")}
            value={data?.trend_last_5_months ? `${Math.round(data.trend_last_5_months)}%` : "-"}
            icon={whichIcon(data?.trend_last_5_months)}
            description={translate.t("low_high_trend", { type: (data?.trend_last_5_months ?? 0) > 0 ? translate.t("dashboard_page.high") : (data?.trend_last_5_months ?? 0) < 0 ? translate.t("dashboard_page.low") : translate.t("dashboard_page.neutral") })}
            styleProps={{ marginBottom: 0, height: "100%", flex: 1 }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AiDemandForecastPanel;
